import { defineStore } from 'pinia'
import { getDefaultUrlMyMega } from "@/helpers/energyHelpers"
import { IOptionList } from '@/interface'
import { IUpdateCustomerContactPayload, IUpdateCustomerCostCenterPayload, IUpdateCustomerInvoicingPayload } from '@/interface/services'
import { getAuthenticationStore, getCommonStore, getContactStore, getContractStore, getInvoicingStore } from "@/helpers/storeHelpers"

import CustomerService from "@/services/customerService"

export const useCustomerStore = defineStore('customer', {
    state: (): ICustomerState => ({
        id: 0,
        guid: '',
        promo: null,
        parrain: null,
        mobileId: null,
        energyId: null,
        completed: false,
        confirmed: false,
        optinMarketing: false,
        optinSales: false,
        conditionsAccepted: false,
        energyLinkConfirmed: false,
        lastUpdate: null,
    }),
    actions: {
        async getUrlMyMega(locale: string): Promise<string> {
            const authStore = getAuthenticationStore()
            if (!authStore || !authStore.isAuthenticated || !authStore.isOwner) {
                return getDefaultUrlMyMega(locale)
            }

            return await CustomerService.relogEnergy()
                .then((response) => { 
                    return getDefaultUrlMyMega(locale, { 
                        "link": "magic",
                        "l": response.login,
                        "u": response.guid,
                        "t": response.token
                    })
                 })
                .catch((error) => {
                    console.error(error)
                    return ''
                })
        },
        async confirmEmail (uuid: string, confirmToken: string) {
            return await CustomerService.confirmEmailPublic(uuid, { token: confirmToken })
                .then((response) => {
                    return response.success
                })
                .catch((error) => {
                    console.error(error)
                    return false
                })
        },
        async initCustomer(id: number): Promise<void> {
            const authStore = getAuthenticationStore()
            const response = await CustomerService.getCustomer(id, true, true)
            this.lastUpdate = new Date();
            this.id = response.customer.id
            this.guid = response.customer.guid
            this.promo = response.customer.promo
            this.parrain = response.customer.parrain
            this.mobileId = response.customer.identifiers.mobileId ?? null
            this.energyId = response.customer.identifiers.energyId ?? null
            this.completed = !!response.customer.status.isComplete 
            this.confirmed = !!response.customer.status.isConfirmed
            this.optinMarketing = !!response.customer.legal.optinMarketing
            this.optinSales = !!response.customer.legal.optinSales
            this.conditionsAccepted = !!response.customer.legal.acceptConditions
            this.energyLinkConfirmed = !!response.customer.legal.energyLinkConfirmed

            if (authStore && authStore.user && authStore.energyRef !== this.energyId) {
                authStore.user.energy_ref = this.energyId
            }

            const contractStore = getContractStore()
            const contactStore = getContactStore()
            const invoicingStore = getInvoicingStore()

            if (response.contracts) {
                contractStore?.initStoreWithValue(response.contracts)
            }

            contactStore?.initStoreWithValue(response.contact)
            invoicingStore?.initStoreWithValue(response.invoicing)
        },
        async updateContact(payload: IUpdateCustomerContactPayload) {
            const response = await CustomerService.updateContact(this.guid, payload)
            this.contact?.initStoreWithValue(response.contact)
        },
        async updateInvoicing(payload: IUpdateCustomerInvoicingPayload) {
            const response = await CustomerService.updateInvoicing(this.guid, payload)
            this.invoicing?.initStoreWithValue(response.invoicing)  
        },       
        async updateCostCenter(payload: IUpdateCustomerCostCenterPayload) {
            await CustomerService.updateCostCenter(this.guid, payload)
        }, 
        async updateLinkStatus() {
            const authStore = getAuthenticationStore()
            const response = await CustomerService.getLinkStatus(this.id)
            this.updateEnergyRef(response.energyId, response.energyLinkConfirmed)
            if (authStore && authStore.user) {
                authStore.user.energy_ref = (this.energyLinkConfirmed && this.energyId ? this.energyId : undefined)
            } 
        },
        getByKey(key?: string) {
            if (!key) {
                return ''
            }
            
            return Reflect.get(this, key)
        },
        updateEnergyRef(energyRef: string|null|undefined, confirmed: boolean): void {
            this.energyId = energyRef ?? null
            this.energyLinkConfirmed = confirmed
            if (!this.energyId || this.energyId == '') {
                this.energyLinkConfirmed = false
            }
        }
    },
    getters: {
        contact(_state: ICustomerState) {
            return getContactStore()
        },
        contract(_state: ICustomerState) {
            return getContractStore()
        },
        invoicing(_state: ICustomerState) {
            return getInvoicingStore()
        },
        contactAddress(_state: ICustomerState) : string|null {
            let address = this?.invoicing?.street ?? ''
            if (address != '' && this?.invoicing?.streetNumber) {
                address += ", " + this?.invoicing?.streetNumber
                if (this?.invoicing?.box) {
                    address += ' ' + this.invoicing.box
                }
            }
            if (address != '') {
                address += ' - ';
            }

            address += this?.invoicing?.zipCode ?? ''
            if (this?.invoicing?.city) {
                address += ' ' + this.invoicing.city
            }
            if (this?.invoicing?.country) {
                address += ' ' + this.invoicing.country
            }
            if (address == '') {
                return null
            }

            return address
        },
        contactBirthDate(_state: ICustomerState) : string|null {
            return this?.contact?.birthDate ?? null
        },
        contactNumber(_state: ICustomerState) : string|null {
            return (this.contact?.mobile && this?.contact?.mobile !== '' ? this?.contact?.mobile : this?.contract?.currentMsisdn) ?? null
        },
        contactNumberFormatted(_state: ICustomerState) : string|null {
            const currentContactNumber = this.contactNumber
            if (!currentContactNumber) {
                return null
            }

            return currentContactNumber.startsWith('0') ? currentContactNumber.substring(1) : '0' + currentContactNumber
        },
        contactEmail(_state: ICustomerState) : string|null {
            return this?.contact?.email ?? null
        },
        contactEmailConfirmed(_state: ICustomerState) : boolean {
            return this?.contact?.isEmailConfirmed ?? true
        },
        contactLanguage(_state: ICustomerState) : string|null {
            return this?.contact?.language ?? null
        },
        contactLanguageOption(_state: ICustomerState) : IOptionList {
            const commonStore = getCommonStore()
            const languages = commonStore?.getTranslatedLanguages ?? []
            return { 
                label: languages.find(e => e.value == (this.contactLanguage ?? 'fr'))?.label ?? '', 
                value: this.contactLanguage ?? 'fr',
            }
        },
        contactFirstLastName(_state: ICustomerState) : string|null {
            return this?.contact?.firstLastName ?? null
        },
        contactLastFirstName(_state: ICustomerState) : string|null {
            return this?.contact?.lastFirstName ?? null
        },
        currentMsisdn(_state: ICustomerState) : string|null {
            return this?.contract?.currentMsisdn ?? null
        },
        paymentIbanNumber(_state: ICustomerState) : string|null {
            return this?.invoicing?.paymentIbanNumber ?? null
        },
        invoicingStreet(_state: ICustomerState) : string|null {
            return this?.invoicing?.street ?? null
        },
        invoicingStreetNumber(_state: ICustomerState) : string|null {
            return this?.invoicing?.streetNumber ?? null
        },
        invoicingBox(_state: ICustomerState) : string|null {
            return this?.invoicing?.box ?? null
        },
        invoicingZipCode(_state: ICustomerState) : string|null {
            return this?.invoicing?.zipCode ?? null
        },
        invoicingCity(_state: ICustomerState) : string|null {
            return this?.invoicing?.city ?? null
        },
        invoicingHolderFirstName(_state: ICustomerState) : string|null {
            return this?.invoicing?.holderFirstName ?? null
        },
        invoicingHolderLastName(_state: ICustomerState) : string|null {
            return this?.invoicing?.holderLastName ?? null
        },
        invoicingEmail(_state: ICustomerState) : string|null {
            return this?.invoicing?.holderEmail ?? null
        },
        paymentBicNumber(_state: ICustomerState) : string|null {
            return this?.invoicing?.paymentBicNumber ?? null
        },
        paymentMode(_state: ICustomerState) : string|null {
            return this?.invoicing?.paymentCurrentMode ?? null
        },
        paymentLastFirstName(_state: ICustomerState) : string|null {
            return this?.invoicing?.lastFirstName ?? null
        }
    },
    persist: true,
})

interface ICustomerState {
    id: number,
    guid: string,
    promo: string|null,
    parrain: string|null,
    mobileId: string|null,
    energyId: string|null,
    completed: boolean,
    confirmed: boolean,
    optinMarketing: boolean,
    optinSales: boolean,
    conditionsAccepted: boolean,
    energyLinkConfirmed: boolean,
    lastUpdate: Date|null
}

export type CustomerStore = ReturnType<typeof useCustomerStore>